<template>
    <v-dialog v-model="showDialog"
              class="citation-dialog"
              min-width="300"
              width="600"
              transition="dialog-bottom-transition">
        <v-card class="text-primary ma-0">
            <v-card-title>
                <span v-if="edit">Редактирование цитаты</span>
                <span v-else>Создание цитаты</span>
            </v-card-title>

            <v-card-text class="pa-4 py-0">
                <v-form ref="form"
                        @keyup.enter="save"
                        @submit.prevent>
                    <v-text-field class="mt-4"
                                  autofocus
                                  clearable
                                  label="Обычный текст"
                                  :rules="textRules"
                                  v-model.trim="text" />
                    <v-text-field class="mt-1"
                                  clearable
                                  label="Ласковый текст"
                                  :rules="sweetTextRules"
                                  v-model.trim="sweetText" />

                    <v-checkbox class="mb-n8"
                                label="Активная"
                                v-model="isActive" />
                    <v-checkbox class="mb-n8"
                                label="Можно показывать утром"
                                v-model="isMorningCitation" />
                    <v-checkbox class="mb-n8"
                                label="Можно показывать вечером"
                                v-model="isEveningCitation" />
                    <v-checkbox class="mb-n4"
                                label="создать нового автора"
                                v-model="createAuthor" />

                    <div v-if="!createAuthor">
                        <v-autocomplete label="Существующий автор"
                                        :rules="selectedAuthorIdRules"
                                        v-model="selectedAuthorId"
                                        item-title="name"
                                        item-value="id"
                                        :items="authors"
                                        clearable
                                        variant="underlined">
                            <template v-slot:chip="{ props, item }">
                                <v-chip v-bind="props"
                                        :text="item.raw.name + ' / ' + item.raw.sweetName + ' (id =' + item.raw.id + ')'" />
                            </template>

                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props"
                                             :title="item.raw.name"
                                             :subtitle="item.raw.sweetName + ' (id =' + item.raw.id + ')'" />
                            </template>
                        </v-autocomplete>
                    </div>
                    <div v-else>
                        <v-text-field class="mt-4"
                                      clearable
                                      label="Обычное имя автора"
                                      :rules="authorNameRules"
                                      v-model.trim="authorName" />
                        <v-text-field class="mt-1"
                                      clearable
                                      label="Ласковое имя автора"
                                      :rules="authorSweetNameRules"
                                      v-model.trim="authorSweetName" />
                    </div>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn color="secondary"
                       variant="elevated"
                       @click="$emit('hide')">Отменить</v-btn>
                <v-btn color="primary"
                       variant="elevated"
                       :disabled="!isValid"
                       @click="save">Сохранить</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    export default {
        components: {

        },

        props: {
            citation: Object,
            edit: Boolean
        },

        emits: ["update:citation", "hide"],

        data() {
            return {
                showDialog: true,

                id: this.citation.id,
                text: this.citation.text,
                sweetText: this.citation.sweetText,
                isActive: this.citation.isActive,
                isMorningCitation: this.citation.isMorningCitation,
                isEveningCitation: this.citation.isEveningCitation,

                selectedAuthorId: this.citation.author?.id && this.citation.author.id > 0
                    ? this.citation.author.id
                    : null,

                authorName: "",
                authorSweetName: "",

                createAuthor: true,

                textRules: [
                    v => !!v || "Нужно задать текст",
                    v => {
                        const item = this.citations.find(e => e.text === v);

                        if (!item || item.id === this.id) {
                            return true;
                        }

                        return "Цитата с таким текстом уже существует";
                    }
                ],
                sweetTextRules: [
                    v => !!v || "Нужно задать ласковый текст",
                    v => {
                        const item = this.citations.find(e => e.sweetText === v);

                        if (!item || item.id === this.id) {
                            return true;
                        }

                        return "Цитата с таким ласковым текстом уже существует";
                    }
                ],
                selectedAuthorIdRules: [
                    v => (this.createAuthor || !!v) || "Нужно выбрать автора"
                ],
                authorNameRules: [
                    v => (!this.createAuthor || !!v) || "Нужно задать имя",
                    v => {
                        if (!this.createAuthor) {
                            return true;
                        }

                        const item = this.authors.find(e => e.name === v);

                        if (!item || item.id === this.id) {
                            return true;
                        }

                        return "Автор с таким именем уже существует";
                    }
                ],
                authorSweetNameRules: [
                    v => (!this.createAuthor || !!v) || "Нужно задать ласковое имя",
                    v => {
                        if (!this.createAuthor) {
                            return true;
                        }

                        const item = this.authors.find(e => e.sweetName === v);

                        if (!item || item.id === this.id) {
                            return true;
                        }

                        return "Автор с таким ласковым именем уже существует";
                    }
                ],

                isValidForm: false
            };
        },

        mounted() {
            this.createAuthor =
                this.citation?.author?.id === 0
                || !this.authors
                || this.authors.length === 0;
        },

        computed: {
            authors() {
                return this.$store.state.admin.authors;
            },
            citations() {
                return this.$store.state.admin.citations;
            },

            selectedAuthor() {
                if (!this.createAuthor) {
                    if (this.selectedAuthorId && this.selectedAuthorId > 0) {
                        return this.authors.find(a => a.id === this.selectedAuthorId);
                    }

                    return null;
                }

                if (this.authorName && this.authorSweetName) {
                    return {
                        id: 0,
                        name: this.authorName,
                        sweetName: this.authorSweetName
                    };
                }

                return null;
            },

            isComplete() {
                return this.text && this.sweetText && this.selectedAuthor;
            },

            isValid() {
                if (this.isComplete) {
                    this.validateForm();
                }

                return this.isComplete && this.isValidForm;
            }
        },

        watch: {
            showDialog(newValue) {
                if (newValue === false) {
                    this.$emit("hide");
                }
            },

            createAuthor() {
                if (this.isComplete) {
                    this.$nextTick(() => this.validateForm());
                }
            }
        },

        methods: {
            async validateForm() {
                if (!this.$refs.form) {
                    return;
                }

                const { valid } = await this.$refs.form.validate();

                this.isValidForm = valid;
            },

            save() {
                if (!this.isValid) {
                    return;
                }

                const citation = {
                    id: this.id,
                    text: this.text,
                    sweetText: this.sweetText,
                    isActive: this.isActive,
                    isMorningCitation: this.isMorningCitation,
                    isEveningCitation: this.isEveningCitation,
                    author: this.selectedAuthor
                };

                this.$emit("hide");
                this.$emit("update:citation", citation);
            }
        }
    };
</script>

<style lang="scss">
    .citation-dialog {
    }
</style>